import {
	type Experience,
	FormFieldKeyName,
	ItineraryQuestionKeyName,
	QuestionKeyName
} from '@/features/experience-shell/types.ts'
import { RemapExperienceForViewOnly } from './types'

export const remapExperienceForViewOnly = (experience: Experience) => {
	const findAnswer = <TQuestionKeyName extends QuestionKeyName>(
		questionKeyName: TQuestionKeyName
	) =>
		experience.expertise.answers.find(
			(answer) => answer.questionKeyName === questionKeyName
		) as
			| Extract<
					Experience['expertise']['answers'][number],
					{ questionKeyName: TQuestionKeyName }
			  >
			| undefined

	const findItineraryAnswer = <
		TQuestionKeyName extends ItineraryQuestionKeyName
	>(
		itineraryId: number,
		questionKeyName: TQuestionKeyName
	) =>
		experience.itineraries
			.find((item) => item.id === itineraryId)
			?.answers.find(
				(answer) => answer.questionKeyName === questionKeyName
			) as
			| Extract<
					Experience['expertise']['answers'][number],
					{ questionKeyName: TQuestionKeyName }
			  >
			| undefined

	const remappedExperience = {
		...experience,
		expertise: {
			...experience.expertise,
			answers: {
				// Background step
				[FormFieldKeyName.enum.expertise_years]: findAnswer(
					QuestionKeyName.enum.expertise_years
				),
				[FormFieldKeyName.enum.online_profile_links]: findAnswer(
					QuestionKeyName.enum.online_profile_links
				),
				[FormFieldKeyName.enum.residential_country_region]: findAnswer(
					QuestionKeyName.enum.residential_country_region
				),
				[FormFieldKeyName.enum.residential_address]: findAnswer(
					QuestionKeyName.enum.residential_address
				),
				[FormFieldKeyName.enum.residential_address_extras]: findAnswer(
					QuestionKeyName.enum.residential_address_extras
				),
				[FormFieldKeyName.enum.residential_state]: findAnswer(
					QuestionKeyName.enum.residential_state
				),
				[FormFieldKeyName.enum.residential_zip]: findAnswer(
					QuestionKeyName.enum.residential_zip
				),
				[FormFieldKeyName.enum.hosting_as_business]: findAnswer(
					QuestionKeyName.enum.hosting_as_business
				),

				// Merchandising step
				[FormFieldKeyName.enum.expertise_summary]: findAnswer(
					QuestionKeyName.enum.expertise_summary
				),
				[FormFieldKeyName.enum.recognition_summary]: findAnswer(
					QuestionKeyName.enum.recognition_summary
				),
				[FormFieldKeyName.enum.listing_title]: findAnswer(
					QuestionKeyName.enum.listing_title
				),
				[FormFieldKeyName.enum.byline]: findAnswer(
					QuestionKeyName.enum.byline
				),
				[FormFieldKeyName.enum.location_description]: findAnswer(
					QuestionKeyName.enum.location_description
				),
				[FormFieldKeyName.enum.guest_activities]: findAnswer(
					QuestionKeyName.enum.guest_activities
				),
				[FormFieldKeyName.enum.host_experience_title]: findAnswer(
					QuestionKeyName.enum.host_experience_title
				),
				[FormFieldKeyName.enum.improved_host_experience_title]:
					findAnswer(
						QuestionKeyName.enum.improved_host_experience_title
					),
				[FormFieldKeyName.enum.host_expertise_description]: findAnswer(
					QuestionKeyName.enum.host_expertise_description
				),
				[FormFieldKeyName.enum.improved_host_expertise_description]:
					findAnswer(
						QuestionKeyName.enum.improved_host_expertise_description
					),
				[FormFieldKeyName.enum.host_recognition_title]: findAnswer(
					QuestionKeyName.enum.host_recognition_title
				),
				[FormFieldKeyName.enum.improved_host_recognition_title]:
					findAnswer(
						QuestionKeyName.enum.improved_host_recognition_title
					),
				[FormFieldKeyName.enum.experience_title]: findAnswer(
					QuestionKeyName.enum.experience_title
				),
				[FormFieldKeyName.enum.improved_experience_title]: findAnswer(
					QuestionKeyName.enum.improved_experience_title
				),
				[FormFieldKeyName.enum.experience_description]: findAnswer(
					QuestionKeyName.enum.experience_description
				),
				[FormFieldKeyName.enum.improved_experience_description]:
					findAnswer(
						QuestionKeyName.enum.improved_experience_description
					),
				// Location step
				[FormFieldKeyName.enum.country_region]: findAnswer(
					QuestionKeyName.enum.country_region
				),
				[FormFieldKeyName.enum.location_address]: findAnswer(
					QuestionKeyName.enum.location_address
				),
				[FormFieldKeyName.enum.location_address_extras]: findAnswer(
					QuestionKeyName.enum.location_address_extras
				),
				[FormFieldKeyName.enum.location_state]: findAnswer(
					QuestionKeyName.enum.location_state
				),
				[FormFieldKeyName.enum.location_zip]: findAnswer(
					QuestionKeyName.enum.location_zip
				),
				[FormFieldKeyName.enum.meeting_point_name]: findAnswer(
					QuestionKeyName.enum.meeting_point_name
				),
				// Pricing discount step
				[FormFieldKeyName.enum.guest_individual_price]: findAnswer(
					QuestionKeyName.enum.guest_individual_price
				),
				[FormFieldKeyName.enum.group_minimum_price]: findAnswer(
					QuestionKeyName.enum.group_minimum_price
				),
				[FormFieldKeyName.enum.instance_private_groups]: findAnswer(
					QuestionKeyName.enum.instance_private_groups
				),
				[FormFieldKeyName.enum.new_listing_discount]: findAnswer(
					QuestionKeyName.enum.new_listing_discount
				),
				[FormFieldKeyName.enum.offer_large_group_discount]: findAnswer(
					QuestionKeyName.enum.offer_large_group_discount
				),
				[FormFieldKeyName.enum.early_bird_discount]: findAnswer(
					QuestionKeyName.enum.early_bird_discount
				),
				[FormFieldKeyName.enum.maximum_public_group_size]: findAnswer(
					QuestionKeyName.enum.maximum_public_group_size
				),
				// Legal step
				[FormFieldKeyName.enum.host_discusses_poi_significance]:
					findAnswer(
						QuestionKeyName.enum.host_discusses_poi_significance
					),
				[FormFieldKeyName.enum.host_serve_alcohol]: findAnswer(
					QuestionKeyName.enum.host_serve_alcohol
				),
				[FormFieldKeyName.enum.host_serve_food]: findAnswer(
					QuestionKeyName.enum.host_serve_food
				),
				[FormFieldKeyName.enum.host_transporting_guests_method]:
					findAnswer(
						QuestionKeyName.enum.host_transporting_guests_method
					),
				[FormFieldKeyName.enum.host_transports_guests]: findAnswer(
					QuestionKeyName.enum.host_transports_guests
				),
				[FormFieldKeyName.enum.involves_national_park]: findAnswer(
					QuestionKeyName.enum.involves_national_park
				),
				[FormFieldKeyName.enum.involves_point_of_interest]: findAnswer(
					QuestionKeyName.enum.involves_point_of_interest
				),
				[FormFieldKeyName.enum.is_licensed_establishment]: findAnswer(
					QuestionKeyName.enum.is_licensed_establishment
				),
				[FormFieldKeyName.enum.prepared_in_restaurant]: findAnswer(
					QuestionKeyName.enum.prepared_in_restaurant
				)
			}
		},
		itineraries: experience.itineraries.map((itinerary) => ({
			...itinerary,
			answers: {
				[ItineraryQuestionKeyName.enum.title]: findItineraryAnswer(
					itinerary.id,
					ItineraryQuestionKeyName.enum.title
				),
				[ItineraryQuestionKeyName.enum.description]:
					findItineraryAnswer(
						itinerary.id,
						ItineraryQuestionKeyName.enum.description
					),
				[ItineraryQuestionKeyName.enum.duration]: findItineraryAnswer(
					itinerary.id,
					ItineraryQuestionKeyName.enum.duration
				),
				[ItineraryQuestionKeyName.enum.improved_title]:
					findItineraryAnswer(
						itinerary.id,
						ItineraryQuestionKeyName.enum.improved_title
					),
				[ItineraryQuestionKeyName.enum.improved_description]:
					findItineraryAnswer(
						itinerary.id,
						ItineraryQuestionKeyName.enum.improved_description
					)
			}
		}))
	}
	return RemapExperienceForViewOnly.parse(remappedExperience)
}
